import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IStudentData, IStudentDetailsFormProps, StudentField, studentFields, StudentPayload, TStudentField } from "../../../interfaces/student";

import { CalenderInput, TextInput } from "../../reusables/FormElements";

import { useSaveStudent } from "../../../hooks/students";

import { getDateString } from "../../../helpers/utility";

const StudentDetailsForm = (props: IStudentDetailsFormProps) => {
  const { operation, isLoading, studentData, formData, setFormData } = props;

  const navigate = useNavigate();

  const [isProcessing, setIsProcessing] = useState(false);

  const { mutate: upsertStudent } = useSaveStudent();

  useEffect(() => {
    if (studentData) {
      setFormData({
        id: studentData.id,
        applicant_id: { value: studentData.applicant_id?.toString() ?? null, error: "" },
        first_name: { value: studentData.first_name, error: "" },
        middle_name: { value: studentData.middle_name ?? "", error: "" },
        last_name: { value: studentData.last_name, error: "" },
        personal_email: { value: studentData.personal_email, error: "" },
        edu_email: { value: studentData.edu_email, error: "" },
        phone: { value: studentData.phone ?? "", error: "" },
        birth_date: { value: studentData.birth_date ? getDateString(studentData.birth_date) : "", error: "" },
      });
    }
    // eslint-disable-next-line
  }, [studentData]);

  const onChangeText = (name: keyof IStudentData, value: string) => {
    setFormData({
      ...formData,
      [name]: { value, error: "" },
    });
  };

  const requiredField: TStudentField[] = ["first_name", "last_name", "personal_email", "edu_email"]

  const isDisabled = () => {
    const emptyFields = requiredField.some((field: TStudentField) => {
      let value: string = formData[field]?.value ?? "";
      return !value.trim()?.length;
    });

    const isDataSame =
      formData.first_name.value === studentData?.first_name &&
      formData.middle_name.value === (studentData?.middle_name ?? "") &&
      formData.last_name.value === studentData?.last_name &&
      formData.personal_email.value === studentData?.personal_email &&
      formData.edu_email.value === studentData?.edu_email &&
      formData.phone.value === (studentData?.phone ?? "") &&
      formData.birth_date.value === getDateString(studentData?.birth_date ?? "");

    return emptyFields || isDataSame || isProcessing || isLoading;
  };

  const onSaveData = () => {
    if (isDisabled()) return;

    setFormData((prevData) => {
      const newData = { ...prevData };
      Object.keys(newData).forEach((key) => {
        if (key === "id") return;
        newData[key as StudentField].error = "";
      });
      return newData;
    });

    setIsProcessing(true);

    const payload = Object.values(studentFields).reduce<StudentPayload>(
      (payload, item) => {
        let value = formData[item].value;

        // @ts-ignore
        payload[item] = value;

        return payload;
      }, {} as StudentPayload
    );

    const completePayload = {
      ...payload,
      id: formData.id,
      school_id: 1,
      birth_date: formData.birth_date.value?.trim()?.length ? new Date(formData.birth_date.value) : null,
    };

    upsertStudent({
      payload: completePayload,
      operation,
      setIsProcessing,
      successCallback: () => {
        navigate('/students')
      },
    });
  };

  return (
    <div className="immersion__details_form">
      <div className="immersion__details_form_inputs__container">
        <div className="immersion__details_form_inputs">
          <TextInput
            name="first_name"
            type="text"
            max_length={100}
            label="First Name"
            required={requiredField.includes("first_name")}
            errorMsg={formData.first_name.error}
            placeholder="First Name"
            onChange={(value: string) => onChangeText("first_name", value)}
            value={formData.first_name.value}
            read_only={isLoading}
          />
          <TextInput
            name="middle_name"
            type="text"
            max_length={100}
            label="Middle Name"
            required={requiredField.includes("middle_name")}
            errorMsg={formData.middle_name.error}
            placeholder="Middle Name"
            onChange={(value: string) => onChangeText("middle_name", value)}
            value={formData.middle_name.value}
            read_only={isLoading}
          />
          <TextInput
            name="last_name"
            type="text"
            max_length={100}
            label="Last Name"
            required={requiredField.includes("last_name")}
            errorMsg={formData.last_name.error}
            placeholder="Last Name"
            onChange={(value: string) => onChangeText("last_name", value)}
            value={formData.last_name.value}
            read_only={isLoading}
          />
          <TextInput
            name="personal_email"
            type="email"
            max_length={100}
            label="Personal Email"
            required={requiredField.includes("personal_email")}
            errorMsg={formData.personal_email.error}
            placeholder="Email address"
            onChange={(value: string) => onChangeText("personal_email", value)}
            value={formData.personal_email.value}
            read_only={isLoading}
          />
          <TextInput
            name="edu_email"
            type="email"
            max_length={100}
            label="Edu Email"
            required={requiredField.includes("edu_email")}
            errorMsg={formData.edu_email.error}
            placeholder="Edu Email address"
            onChange={(value: string) => onChangeText("edu_email", value)}
            value={formData.edu_email.value}
            read_only={isLoading}
          />
          <CalenderInput
            name="birth_date"
            label="Date of Birth"
            required={requiredField.includes("birth_date")}
            errorMsg={formData.birth_date.error}
            placeholder="mm/dd/yyyy"
            format="mm/dd/yyyy"
            onChange={(value: string) => onChangeText("birth_date", value)}
            value={formData.birth_date.value}
            read_only={isLoading}
          />
          <TextInput
            name="phone"
            type="text"
            label="Phone"
            errorMsg={formData.phone.error}
            required={requiredField.includes("phone")}
            pattern_mask="phone_number"
            max_length={12}
            placeholder="650-000-0000"
            onChange={(value: string) => onChangeText("phone", value)}
            value={formData.phone.value}
            read_only={isLoading}
          />
        </div>
      </div>
      <div className="immersion__details_form_actions">
        <button
          className={`immersion__btn immersion__btn__secondary ${isProcessing ? "immersion__btn__secondary__disabled" : ""}`}
          onClick={() => (isProcessing ? null : navigate(`/students`))}
        >
          {operation === "create" ? "Discard" : "Go back"}
        </button>
        <button
          className={`immersion__btn immersion__btn__primary ${isDisabled() ? "immersion__btn__primary__disabled" : ""}`}
          onClick={onSaveData}
        >
          {isProcessing ? <i className="immersion__btn_loader" /> : "Save"}
        </button>
      </div>
    </div>
  );
};

export default StudentDetailsForm;
